import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { interval, Subject } from 'rxjs';

import { environment } from '../../environments/environment';
import { PageService } from './page.service';
import { WsSubscription } from './ws-subscription';

/**
 * This service handles the connection to the websocket.
 *
 * It reuses alcmeon-prod's reconnecting websocket
 *
 * The websocket notifies us when we should pull new messages to the conversation.
 *
 * If no notifications have been recieved in the last 30 seconds, we notify the chat that it should
 * call the backend, just in case the websocket server is having a hard time.
 * If the websocket gets disconnected for some reason, we poll every 5 seconds
 *
 * Note that this are the polling delay after a messasge has been sent/received;
 * afterwards the delay is doubled every time we poll, up to 16 times the original delay
 * The delay is reset when we do send/receive a message
 */

const POLL_PERIOD_MS = 3000; // poll every 3s (with exponential backoff) even if websocket doesn't suggest us to
const POLL_PERIOD_NO_WEBSOCKET_MS = 1000; // poll every second (with exponential backoff) if webscoket is disconnected
const TIMER_INTERVAL_MS = 1000;

@Injectable()
export class SocketService {
  private API_SERVER_URL = environment.apiURLPrefix;
  private WEBSOCKET_URL = environment.websocketURL;

  private refreshNotification$ = new Subject<MessageEvent>();
  private isConnected = false;
  private lastWebsocketNotifTimestamp = Date.now(); // the chat component will fetch the first 10 messages now

  private currentPollPeriodMs: number;
  private currentPollPeriodNoWebsocketMs: number;

  // class properties from reconnecting-websocket.js
  private _delayS = 16;
  private _maxDelayS = 128;
  private _webSocket: WebSocket = null;
  private _timeout = null;
  private _closing = false;

  constructor(private http: HttpClient, private pageService: PageService) {
    this.resetExponentialBackoff();
  }

  public init(): void {
    // Get subscription id and connect websocket
    this._subscribeAndOpen();

    // Run a 1s timer that will check if the websocket has been inactive for long enough
    // that it warrants a refresh of messages
    interval(TIMER_INTERVAL_MS).subscribe(() => {
      const period = this.isConnected ? this.currentPollPeriodMs : this.currentPollPeriodNoWebsocketMs;
      if (Date.now() - this.lastWebsocketNotifTimestamp > period) {
        this.lastWebsocketNotifTimestamp = Date.now();
        this.increaseExponentialBackoff();
        this.refreshNotification$.next(null);
      }
    });
  }

  /**
   * Subscription method for the chat component
   */
  public subscribeToRefreshNotifications(observer: (messageEvent: MessageEvent) => void) {
    this.refreshNotification$.subscribe(observer);
  }

  public resetExponentialBackoff() {
    this.currentPollPeriodMs = POLL_PERIOD_MS;
    this.currentPollPeriodNoWebsocketMs = POLL_PERIOD_NO_WEBSOCKET_MS;
  }

  private increaseExponentialBackoff() {
    if (this.currentPollPeriodMs < 16 * POLL_PERIOD_MS) {
      this.currentPollPeriodMs *= 2;
      this.currentPollPeriodNoWebsocketMs *= 2;
    }
  }

  public close() {
    this.isConnected = false;
    this._closing = true;
    this._clearListenersAndClose();
  }

  //
  // private reconnecting websocket implementation
  //

  private _clearListenersAndClose() {
    if (this._webSocket !== null) {
      this._webSocket.onerror = function () {};
      this._webSocket.onclose = function () {};
      this._webSocket.onmessage = function () {};
      this._webSocket.close();
    }
  }

  private restart() {
    if (this._closing) {
      // make sure we do not restart the old connection upon page reload
      return;
    }
    this.isConnected = false; // something went wrong, let's change the polling period
    if (this._timeout === null) {
      const delayMs = Math.random() * this._delayS * 1000;
      this._timeout = setTimeout(() => {
        this._subscribeAndOpen();
        this._timeout = null;
      }, delayMs);
      this._delayS *= 2;
      this._delayS = Math.min(this._delayS, this._maxDelayS);
    }
  }

  private _subscribeAndOpen() {
    const endpoint = this.API_SERVER_URL + this.pageService.getPageId() + '/subscriptions';
    this.http.post<WsSubscription>(endpoint, null).subscribe({
      next: ({ ws_jwt_token }) => this.connectWebSocket(ws_jwt_token),
      error: () => this.restart(),
    });
  }

  private connectWebSocket(ws_jwt_token: string) {
    const endpoint = `${this.WEBSOCKET_URL}?ws_jwt_token=${ws_jwt_token}`;

    // make sure we do not get spurious onerror/onclose messages
    this._clearListenersAndClose();

    this._webSocket = new WebSocket(endpoint);

    // assume the connection worked;
    // we'll update isConnected in restart if it actually dind't
    this.isConnected = true;

    // It is not really possible to distinguish between normal and erronous closes
    // because different browsers report different events (onclose and/or onerror)
    // so we handle both events just the same.
    this._webSocket.onerror = () => this.restart();
    this._webSocket.onclose = () => this.restart();
    this._webSocket.onmessage = messageEvent => {
      // We should reset delayS = 2 in onopen but onopen is invoked
      // just before onerror by some browsers
      // so we can't rely on onopen as a meaningful signal of "ok".
      // Instead, we consider we need to get at least one event on
      // a websocket to be good.
      this._delayS = 2;
      this.refreshNotification$.next(messageEvent);
    };
  }
}
